/**
 * CRA conformity — one product.
 *
 * Three things, in the order a manufacturer works through them:
 *   1. CE readiness — the checklist of what this product needs for a CE mark,
 *      each item citing its CRA Annex / Article, with a verdict at the top.
 *   2. Vulnerabilities — the coordinated-disclosure record (Annex I Part II),
 *      with the Article 14 24h / 72h clocks and overdue highlighting for any
 *      actively-exploited vulnerability.
 *   3. The EU Declaration of Conformity — generated as a PDF on demand.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CRA_VULN_SEVERITIES, CRA_VULN_STATUSES,
  formatDuration,
  type CeChecklistItem, type CeReadiness, type CraDeadlineView, type CraDeadlineState,
} from '@opencomply/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { downloadFile } from '../lib/download';
import {
  Card, CardHeader, Button, Pill, Modal, Field, Skeleton, ErrorNotice, type Tone,
} from '../components/ui';
import { useTick } from '../components/IncidentClocks';
import { useCraT, type CraKey } from '../lib/craStrings';
import { cn } from '../lib/cn';
import { READINESS_TONE, ProductModal, type CraProductRow } from './CraConformity';

interface VulnRow {
  id: string;
  identifier: string | null;
  title: string;
  severity: string;
  status: string;
  activelyExploited: boolean;
  reportedToEnisa: boolean;
  earlyWarningAt: string | null;
  notificationAt: string | null;
  finalReportAt: string | null;
  remediation: string | null;
  deadlines: CraDeadlineView[];
}

interface ProductDetail extends CraProductRow {
  description: string | null;
  sbomRef: string | null;
  createdAt: string;
  updatedAt: string;
  readinessDetail: CeReadiness;
  vulnerabilities: VulnRow[];
}

const ITEM_TONE: Record<string, Tone> = {
  MET: 'good', PARTIAL: 'warning', MISSING: 'critical', NOT_APPLICABLE: 'neutral',
};
const SEVERITY_TONE: Record<string, Tone> = {
  LOW: 'neutral', MEDIUM: 'warning', HIGH: 'serious', CRITICAL: 'critical',
};

export function CraProductDetailPage() {
  const { id = '' } = useParams();
  const { tx } = useCraT();
  const { t } = useTranslation();
  const { allows } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [vulnModal, setVulnModal] = useState<{ open: boolean; vuln?: VulnRow }>({ open: false });
  const [declError, setDeclError] = useState<string | null>(null);

  // The response includes `readiness` (a CeReadiness object) and raw fields.
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['cra-product', id],
    queryFn: () => api.get<ProductDetail & { readiness: CeReadiness }>(`/cra-conformity/products/${id}`),
    staleTime: 15_000,
  });

  const manage = allows('cra.manage');

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/cra-conformity/products/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cra-products'] });
      navigate('/cra-conformity');
    },
  });

  const downloadDeclaration = async () => {
    setDeclError(null);
    try {
      const controller = new AbortController();
      await downloadFile(
        `/cra-conformity/products/${id}/declaration`,
        'declaration-of-conformity.pdf',
        '/cra-conformity/products',
        controller.signal,
      );
    } catch {
      setDeclError(tx('cra.declaration.failed'));
    }
  };

  if (isLoading) return <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto"><Skeleton className="h-64 w-full" /></div>;
  if (error || !data) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <ErrorNotice message={tx('cra.loadFailed')} onRetry={() => void refetch()} retryLabel={tx('cra.retry')} />
      </div>
    );
  }

  const readiness = (data.readiness ?? data.readinessDetail) as CeReadiness;

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto space-y-5">
      <button onClick={() => navigate('/cra-conformity')} className="text-sm text-ink-muted hover:text-ink">
        ← {tx('cra.back')}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="oc-h1">{data.name}</h1>
            {data.version && <span className="font-mono text-sm text-ink-muted">{data.version}</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <Pill tone="neutral">{tx(`cra.category.${data.category}` as CraKey)}</Pill>
            <Pill tone="neutral">{tx(`cra.route.${data.conformityRoute}` as CraKey)}</Pill>
            <Pill tone="neutral">{tx(`cra.status.${data.status}` as CraKey)}</Pill>
            <Pill tone={READINESS_TONE[readiness.status]}>{tx(`cra.readiness.${readiness.status}` as CraKey)}</Pill>
          </div>
        </div>
        {manage && (
          <div className="flex gap-2">
            <Button onClick={() => setEditOpen(true)}>{t('common.edit')}</Button>
            <Button
              variant="danger"
              onClick={() => { if (window.confirm(tx('cra.delete.confirm'))) deleteMutation.mutate(); }}
            >
              {tx('cra.delete')}
            </Button>
          </div>
        )}
      </div>

      {data.description && <p className="oc-muted max-w-3xl">{data.description}</p>}

      {/* --- CE readiness --- */}
      <Card>
        <CardHeader
          title={tx('cra.readiness.title')}
          subtitle={tx('cra.readiness.summary', { met: readiness.metCount, total: readiness.applicableCount })}
          action={
            <Button variant="primary" onClick={() => void downloadDeclaration()}>
              {tx('cra.declaration.generate')}
            </Button>
          }
        />
        {declError && <p role="alert" className="text-sm text-critical mb-2">{declError}</p>}
        <ul className="divide-y divide-line">
          {readiness.items.map((item: CeChecklistItem) => (
            <li key={item.key} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink">{tx(`cra.check.${item.key}` as CraKey)}</p>
                <p className="text-xs text-ink-muted">
                  {item.reference}{item.detail ? ` · ${item.detail}` : ''}
                </p>
              </div>
              <Pill tone={ITEM_TONE[item.status] ?? 'neutral'}>{tx(`cra.item.${item.status}` as CraKey)}</Pill>
            </li>
          ))}
        </ul>
        <p className="text-xs text-ink-muted mt-3">{tx('cra.legal.note')}</p>
      </Card>

      {/* --- Vulnerabilities --- */}
      <Card>
        <CardHeader
          title={tx('cra.vuln.title')}
          subtitle={tx('cra.vuln.subtitle')}
          action={manage ? <Button variant="primary" onClick={() => setVulnModal({ open: true })}>{tx('cra.vuln.add')}</Button> : undefined}
        />
        {data.vulnerabilities.length === 0 ? (
          <p className="text-sm text-ink-muted py-4">{tx('cra.vuln.empty')}</p>
        ) : (
          <div className="space-y-2">
            {data.vulnerabilities.map((v) => (
              <VulnCard key={v.id} vuln={v} onEdit={manage ? () => setVulnModal({ open: true, vuln: v }) : undefined} />
            ))}
          </div>
        )}
      </Card>

      <ProductModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        product={data}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ['cra-product', id] });
          void queryClient.invalidateQueries({ queryKey: ['cra-products'] });
          setEditOpen(false);
        }}
      />

      <VulnModal
        key={vulnModal.vuln?.id ?? 'new'}
        open={vulnModal.open}
        productId={id}
        vuln={vulnModal.vuln}
        onClose={() => setVulnModal({ open: false })}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ['cra-product', id] });
          setVulnModal({ open: false });
        }}
      />
    </div>
  );
}

/** One vulnerability, with its Article 14 clocks if it is actively exploited. */
function VulnCard({ vuln, onEdit }: { vuln: VulnRow; onEdit?: () => void }) {
  const { tx, formatDateTime } = useCraT();
  const now = useTick(vuln.activelyExploited);
  const missed = vuln.deadlines.some((d) => d.state === 'OVERDUE' || d.state === 'SUBMITTED_LATE');

  return (
    <div className={cn(
      'rounded-xl border p-3 sm:p-4',
      missed ? 'border-critical/40 border-l-4 border-l-critical bg-critical/[0.04]' : 'border-line',
    )}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {vuln.identifier && <span className="font-mono text-xs text-ink-muted">{vuln.identifier}</span>}
            <Pill tone={SEVERITY_TONE[vuln.severity] ?? 'neutral'}>{tx(`cra.severity.${vuln.severity}` as CraKey)}</Pill>
            <Pill tone={vuln.status === 'OPEN' ? 'warning' : 'good'}>{tx(`cra.vulnStatus.${vuln.status}` as CraKey)}</Pill>
            {vuln.activelyExploited && <Pill tone="critical">{tx('cra.vuln.field.activelyExploited')}</Pill>}
            {vuln.reportedToEnisa && <Pill tone="good">{tx('cra.vuln.field.reportedToEnisa')}</Pill>}
            {missed && <Pill tone="critical">{tx('cra.deadline.missed')}</Pill>}
          </div>
          <p className="font-medium text-ink mt-1 text-balance">{vuln.title}</p>
          {vuln.remediation && <p className="text-xs text-ink-muted mt-0.5">{vuln.remediation}</p>}
        </div>
        {onEdit && <Button size="sm" onClick={onEdit}>{tx('cra.vuln.edit.title')}</Button>}
      </div>

      {vuln.deadlines.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {vuln.deadlines.map((d) => <DeadlineChip key={d.stage} view={d} now={now} />)}
        </div>
      )}
    </div>
  );
}

function deadlineTone(state: CraDeadlineState): Tone {
  switch (state) {
    case 'OVERDUE': case 'SUBMITTED_LATE': return 'critical';
    case 'DUE_SOON': return 'warning';
    case 'SUBMITTED_ON_TIME': return 'good';
    default: return 'neutral';
  }
}

/** One Article 14 clock, recomputed locally so it ticks without polling. */
function DeadlineChip({ view, now }: { view: CraDeadlineView; now: number }) {
  const { tx } = useCraT();
  const stage = tx(`cra.stage.${view.stage}` as CraKey);
  const limit = tx(`cra.stageLimit.${view.stage}` as CraKey);

  let label: string;
  if (view.reportedAt) {
    label = view.state === 'SUBMITTED_LATE' ? tx('cra.deadline.late') : tx('cra.deadline.onTime');
  } else {
    const msRemaining = new Date(view.dueAt).getTime() - now;
    label = msRemaining <= 0
      ? tx('cra.deadline.overdue', { duration: formatDuration(Math.abs(msRemaining)) })
      : tx('cra.deadline.open', { duration: formatDuration(msRemaining) });
  }
  const state: CraDeadlineState = !view.reportedAt && new Date(view.dueAt).getTime() - now <= 0 ? 'OVERDUE' : view.state;

  return (
    <span className="inline-flex items-center gap-1.5">
      <Pill tone={deadlineTone(state)}>{stage} · {limit}</Pill>
      <span className="text-xs text-ink-muted">{label}</span>
    </span>
  );
}

/** Add / edit a vulnerability. */
function VulnModal({
  open, productId, vuln, onClose, onSaved,
}: {
  open: boolean;
  productId: string;
  vuln?: VulnRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { tx } = useCraT();
  const { t } = useTranslation();
  const editing = !!vuln;

  const [title, setTitle] = useState(vuln?.title ?? '');
  const [identifier, setIdentifier] = useState(vuln?.identifier ?? '');
  const [severity, setSeverity] = useState(vuln?.severity ?? 'MEDIUM');
  const [status, setStatus] = useState(vuln?.status ?? 'OPEN');
  const [activelyExploited, setActivelyExploited] = useState(vuln?.activelyExploited ?? false);
  const [reportedToEnisa, setReportedToEnisa] = useState(vuln?.reportedToEnisa ?? false);
  const [remediation, setRemediation] = useState(vuln?.remediation ?? '');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        title: title.trim(),
        identifier: identifier.trim() || null,
        severity, status, activelyExploited, reportedToEnisa,
        remediation: remediation.trim() || null,
      };
      return editing
        ? api.patch(`/cra-conformity/products/${productId}/vulnerabilities/${vuln!.id}`, body)
        : api.post(`/cra-conformity/products/${productId}/vulnerabilities`, body);
    },
    onSuccess: () => { setError(null); onSaved(); },
    onError: (err) => setError(err instanceof ApiError ? err.code : 'UNKNOWN_ERROR'),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? tx('cra.vuln.edit.title') : tx('cra.vuln.create.title')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!title.trim() || mutation.isPending} onClick={() => mutation.mutate()}>
            {tx('cra.save')}
          </Button>
        </>
      }
    >
      <Field label={tx('cra.vuln.field.title')} required htmlFor="vuln-title">
        <input id="vuln-title" className="oc-input" value={title} maxLength={500} autoFocus onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label={tx('cra.vuln.field.identifier')} htmlFor="vuln-id">
        <input id="vuln-id" className="oc-input" value={identifier} maxLength={64} placeholder="CVE-2026-0000" onChange={(e) => setIdentifier(e.target.value)} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={tx('cra.vuln.field.severity')} htmlFor="vuln-sev">
          <select id="vuln-sev" className="oc-input" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {CRA_VULN_SEVERITIES.map((s) => <option key={s} value={s}>{tx(`cra.severity.${s}` as CraKey)}</option>)}
          </select>
        </Field>
        <Field label={tx('cra.vuln.field.status')} htmlFor="vuln-status">
          <select id="vuln-status" className="oc-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            {CRA_VULN_STATUSES.map((s) => <option key={s} value={s}>{tx(`cra.vulnStatus.${s}` as CraKey)}</option>)}
          </select>
        </Field>
      </div>
      <Field label={tx('cra.vuln.field.remediation')} htmlFor="vuln-rem">
        <textarea id="vuln-rem" className="oc-input min-h-20" value={remediation} maxLength={20000} onChange={(e) => setRemediation(e.target.value)} />
      </Field>
      <label className="flex items-center gap-2 text-sm text-ink mt-1">
        <input type="checkbox" checked={activelyExploited} onChange={(e) => setActivelyExploited(e.target.checked)} />
        {tx('cra.vuln.field.activelyExploited')}
      </label>
      <p className="text-xs text-ink-muted ml-6 -mt-1">{tx('cra.vuln.field.activelyExploitedHint')}</p>
      <label className="flex items-center gap-2 text-sm text-ink mt-2">
        <input type="checkbox" checked={reportedToEnisa} onChange={(e) => setReportedToEnisa(e.target.checked)} />
        {tx('cra.vuln.field.reportedToEnisa')}
      </label>

      {error && <p role="alert" className="text-sm text-critical mt-2">{tx(`cra.error.${error}` as CraKey, { defaultValue: error })}</p>}
    </Modal>
  );
}
